import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Code as ConnectCode, ConnectError } from '@connectrpc/connect';
import { SpecraButton, SpecraEyebrow, SpecraHeading, SpecraIconTile, SpecraInlineBanner, SpecraKeyValueRow, SpecraSurface, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Check, FileText, PencilLine, Presentation, Sheet } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { asOfficeDocType, useOfficeDocumentDrawer } from '../../../contexts/OfficeDocumentDrawerContext';
import { agentClient } from '../../../lib/connect/clients';
import { useChatFeedback } from '../ChatFeedbackContext';
import { isInlineOfficeBytes } from '../parseDraftOffice';
import { PROPOSAL_DECIDED_EVENT, recalledDecision, rememberDecision } from './proposalDecisions';
const TYPE_LABEL = {
    docx: 'Word document',
    xlsx: 'Excel workbook',
    pptx: 'PowerPoint deck'
};
function TypeIcon({ type }) {
    if (type === 'xlsx') return /*#__PURE__*/ _jsx(Sheet, {
        className: "h-4 w-4"
    });
    if (type === 'pptx') return /*#__PURE__*/ _jsx(Presentation, {
        className: "h-4 w-4"
    });
    return /*#__PURE__*/ _jsx(FileText, {
        className: "h-4 w-4"
    });
}
// A plain sentence for a refused or failed create. A refusal returns the card
// to pending; anything else is safe to retry because Accept is idempotent.
function createErrorMessage(err, destination) {
    if (err instanceof ConnectError) {
        switch(err.code){
            case ConnectCode.PermissionDenied:
                return `You can't save a document to ${destination}. Reject this, or ask the assistant to save it to your own Documents.`;
            case ConnectCode.NotFound:
                return 'The draft or the destination no longer exists. Ask the assistant to make it again.';
            case ConnectCode.FailedPrecondition:
                return err.rawMessage || 'This document cannot be created any more.';
            default:
                return 'The document could not be created right now. Retry — it will not be created twice.';
        }
    }
    return 'The document could not be created right now. Retry — it will not be created twice.';
}
/**
 * The create mode of the document review card: names the document, what it
 * holds and where it will be saved, and creates it only when the user accepts
 * (as the user, server-side, once — a retried Accept returns the same file).
 */ export function DocumentCreateCard({ output, toolCallId }) {
    const { openOffice, setProposal } = useOfficeDocumentDrawer();
    const sendFeedback = useChatFeedback();
    // A decision taken in this session (this card remounted, or another copy of
    // it) wins over the stream's stale output; the saved state covers a reload.
    const recalled = recalledDecision(toolCallId);
    const saved = recalled?.decision ?? output.approvalState;
    const [status, setStatus] = useState(saved === 'accepted' ? 'accepted' : saved === 'rejected' ? 'rejected' : 'pending');
    const [error, setError] = useState(null);
    const createdId = recalled?.documentId || output.createdDocumentId;
    const [created, setCreated] = useState(createdId ? {
        id: createdId,
        name: recalled?.documentName || output.createdDocumentName || output.name || ''
    } : null);
    useEffect(()=>{
        const onDecided = ()=>{
            const d = recalledDecision(toolCallId);
            if (!d) return;
            setStatus(d.decision);
            setError(null);
            if (d.documentId) setCreated({
                id: d.documentId,
                name: d.documentName || ''
            });
        };
        window.addEventListener(PROPOSAL_DECIDED_EVENT, onDecided);
        return ()=>window.removeEventListener(PROPOSAL_DECIDED_EVENT, onDecided);
    }, [
        toolCallId
    ]);
    // A same-frame double click sees stale state; the ref blocks the second run.
    const actingRef = useRef(false);
    const name = output.name || 'Untitled';
    const destination = output.destination || 'Documents';
    const typeLabel = TYPE_LABEL[output.documentType ?? ''] ?? 'Office document';
    const docType = asOfficeDocType(output.documentType);
    const decidable = Boolean(output.approvalConversationId && toolCallId);
    const previewId = `create-preview:${toolCallId || name}`;
    const summary = output.contents && !isInlineOfficeBytes(output.contents) ? output.contents : undefined;
    // A blank create (a Word document the assistant writes after Accept) has no
    // file yet, so Preview would only say it is unavailable. Offer it only when
    // there are draft bytes to show.
    const hasDraft = Boolean(output.draftArtifactId) || isInlineOfficeBytes(output.contents);
    const openDraftPreview = ()=>{
        if (!setProposal) return;
        setProposal(previewId, {
            documentId: previewId,
            operations: [],
            draft: {
                contents: output.contents,
                draftArtifactId: output.draftArtifactId,
                sizeBytes: output.sizeBytes
            }
        });
        openOffice(previewId, {
            title: name,
            docType,
            previewOnly: true
        });
    };
    if (output.success === false) {
        return /*#__PURE__*/ _jsx(SpecraInlineBanner, {
            variant: "framed",
            tone: "danger",
            "data-testid": Testids.assistant.documentCreateCard,
            children: output.message || 'The document could not be proposed.'
        });
    }
    const decide = async (decision)=>{
        if (actingRef.current || status !== 'pending' || !decidable) return;
        actingRef.current = true;
        setStatus('working');
        setError(null);
        try {
            const response = await agentClient.decideDocumentProposal({
                conversationId: output.approvalConversationId,
                toolCallId: toolCallId,
                decision
            });
            if (response.decision === 'rejected') {
                rememberDecision(toolCallId, {
                    decision: 'rejected'
                });
                setStatus('rejected');
                sendFeedback(`User declined creating the ${typeLabel} "${name}"; nothing was created. Ask what they want instead.`);
                return;
            }
            if (response.decision !== 'accepted' || !response.documentId) {
                throw new Error('The decision could not be confirmed.');
            }
            const doc = {
                id: response.documentId,
                name: response.documentName || name
            };
            rememberDecision(toolCallId, {
                decision: 'accepted',
                documentId: doc.id,
                documentName: doc.name
            });
            setCreated(doc);
            setStatus('accepted');
            openOffice(doc.id, {
                title: doc.name,
                docType
            });
            const ids = response.volumeDocumentId ? `documentId=${response.volumeDocumentId} (volumeId=${output.volumeId ?? ''})` : `documentId=${doc.id}`;
            sendFeedback(`User accepted: the ${typeLabel} "${doc.name}" now exists in ${destination} (${ids}). Continue with what they asked.`);
        } catch (err) {
            setStatus('pending');
            setError(createErrorMessage(err, destination));
        } finally{
            actingRef.current = false;
        }
    };
    const accepted = status === 'accepted';
    return /*#__PURE__*/ _jsxs(SpecraSurface, {
        "data-testid": Testids.assistant.documentCreateCard,
        "data-state": status,
        elevation: "xs",
        className: "overflow-hidden",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-start gap-3 px-4 pb-3 pt-3.5",
                children: [
                    /*#__PURE__*/ _jsx(SpecraIconTile, {
                        tone: accepted ? 'success' : 'info',
                        children: /*#__PURE__*/ _jsx(TypeIcon, {
                            type: output.documentType
                        })
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "min-w-0 flex-1",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraEyebrow, {
                                children: accepted ? 'Document created' : `New ${typeLabel} · not created yet`
                            }),
                            /*#__PURE__*/ _jsx(SpecraHeading, {
                                size: "sm",
                                className: "mt-1 truncate",
                                title: name,
                                children: name
                            }),
                            summary ? /*#__PURE__*/ _jsx(SpecraText, {
                                size: "micro",
                                tone: "muted",
                                className: "mt-0.5 block",
                                children: summary
                            }) : null
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "border-t border-border px-4 py-2.5",
                children: /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                    label: accepted ? 'Saved in' : 'Will be saved in',
                    value: /*#__PURE__*/ _jsx("span", {
                        "data-testid": Testids.assistant.documentCreateDestination,
                        children: destination
                    })
                })
            }),
            error ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                variant: "band",
                tone: "danger",
                "data-testid": Testids.assistant.documentCreateStatus,
                children: error
            }) : null,
            status === 'pending' || status === 'working' ? /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-center gap-2 border-t border-border bg-muted/40 px-4 py-3",
                children: [
                    hasDraft ? /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "secondary",
                        "data-testid": Testids.assistant.documentCreatePreview,
                        onClick: openDraftPreview,
                        disabled: status === 'working',
                        children: "Preview"
                    }) : null,
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": Testids.assistant.documentCreateAccept,
                        onClick: ()=>void decide('accepted'),
                        disabled: !decidable,
                        isLoading: status === 'working',
                        iconLeft: /*#__PURE__*/ _jsx(Check, {
                            className: "h-4 w-4"
                        }),
                        children: "Create document"
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "secondary",
                        "data-testid": Testids.assistant.documentCreateReject,
                        onClick: ()=>void decide('rejected'),
                        disabled: !decidable || status === 'working',
                        children: "Don't create"
                    }),
                    /*#__PURE__*/ _jsx(SpecraText, {
                        size: "micro",
                        tone: "muted",
                        className: "ml-auto",
                        children: decidable ? 'Nothing is saved until you choose.' : 'This earlier proposal has no saved review status. Ask for it again.'
                    })
                ]
            }) : null,
            accepted ? /*#__PURE__*/ _jsxs("div", {
                "data-testid": Testids.assistant.documentCreateStatus,
                className: "flex flex-wrap items-center gap-2 border-t border-border bg-muted/40 px-4 py-3",
                children: [
                    /*#__PURE__*/ _jsx(Check, {
                        className: "h-3.5 w-3.5 text-success",
                        "aria-hidden": true
                    }),
                    /*#__PURE__*/ _jsxs(SpecraText, {
                        size: "micro",
                        className: "font-medium",
                        children: [
                            "Created “",
                            created?.name || name,
                            "” in ",
                            destination
                        ]
                    }),
                    created ? /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        variant: "secondary",
                        className: "ml-auto",
                        "data-testid": Testids.assistant.documentCreateOpen,
                        onClick: ()=>openOffice(created.id, {
                                title: created.name,
                                docType
                            }),
                        iconLeft: /*#__PURE__*/ _jsx(PencilLine, {
                            className: "h-3.5 w-3.5"
                        }),
                        children: "Open"
                    }) : null
                ]
            }) : null,
            status === 'rejected' ? /*#__PURE__*/ _jsx("div", {
                "data-testid": Testids.assistant.documentCreateStatus,
                className: "border-t border-border bg-muted/40 px-4 py-3",
                children: /*#__PURE__*/ _jsx(SpecraText, {
                    size: "micro",
                    tone: "muted",
                    children: "Not created — nothing was saved."
                })
            }) : null
        ]
    });
}
